import * as types from '../types.js';
import db from '../../versa/db.js';
import { bindFormFieldTitleSync, syncFormFieldTitles } from '../utils/form-field-titles.js';

function _parseSialNum(s) {
	const c = String(s ?? '').replace(/\./g, '').replace(',', '.');
	const n = parseFloat(c);
	return isFinite(n) ? n : 0;
}

function _fmtBRL(v) {
	return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function recalcOrderTotals(root) {
	if (!root) return;
	const tbody = root.querySelector('.sial-items-scroll tbody');
	const totalEl = root.querySelector('#io-grand-total');
	if (!tbody) return;
	let grand = 0;
	tbody.querySelectorAll('tr.row').forEach((row) => {
		const qEl = row.querySelector('[id^="quantity-"]');
		if (!qEl) return;
		const n = (qEl.id.match(/\d+$/) || ['0'])[0];
		const qty   = _parseSialNum(qEl.value);
		const price = _parseSialNum(row.querySelector(`#unit_price-${n}`)?.value);
		const total = qty * price;
		const ti = row.querySelector(`#item_total-${n}`);
		if (ti) ti.value = (qty > 0 || price > 0) ? _fmtBRL(total) : '';
		grand += total;
	});
	if (totalEl)
		totalEl.textContent = grand > 0 ? `Total do Pedido: ${_fmtBRL(grand)}` : '';
}

async function syncStatusApprovalLock(root)
{
	if (!root) return;
	const statusEl = root.querySelector('#status');
	const companyEl = root.querySelector('#company_id');
	if (!statusEl) return;
	const companyId = companyEl?.value;
	let requireApproval = false;
	if (companyId) {
		try {
			const company = await db.get('companies', companyId)
				|| await db.get('companies', [companyId]);
			requireApproval = !!(company && company.input_orders_require_approval);
		} catch (_) {
			requireApproval = false;
		}
	}
	statusEl.readOnly = requireApproval;
	if (requireApproval) {
		statusEl.title = 'Status alterado apenas pelas operações Aprovar ou Rejeitar (parâmetro da empresa).';
		statusEl.setAttribute('aria-readonly', 'true');
	} else {
		statusEl.title = 'Status do pedido';
		statusEl.removeAttribute('aria-readonly');
	}
}

/** #MT-3AD0979 — trava quando há Entrada ativa: frete/transportador/condição editáveis. */
function applyInputOrderEditLock(fs)
{
	const form = document.querySelector('#order');
	const banner = document.querySelector('#io-lock-banner');
	if (!form) return;
	const vals = fs.form.getValues();
	const locked = vals.edit_locked_by_input === true || vals.edit_locked_by_input === 1
		|| vals.edit_locked_by_input === '1' || String(vals.edit_locked_by_input).toLowerCase() === 'true';
	form.classList.toggle('input-order--edit-locked', locked);
	if (banner) banner.style.display = locked ? 'block' : 'none';
	const toolbar = form.querySelector('.io-footer-tools');
	const rowImgs = toolbar ? toolbar.querySelectorAll('img') : [];
	const allowSelectors = [
		'#carrier_id', '#freight_type', '#freight_value',
		'#payment_installment_count', '#payment_base_date',
		'#io_prazo_0', '#io_prazo_1', '#io_prazo_2', '#io_prazo_3',
		'#io_prazo_4', '#io_prazo_5', '#io_prazo_6', '#io_prazo_7',
		'#io_prazo_8', '#io_prazo_9', '#io_prazo_10', '#io_prazo_11',
	];
	if (!locked)
	{
		form.querySelectorAll('[data-io-lock-disabled]').forEach((el) =>
		{
			el.removeAttribute('data-io-lock-disabled');
			el.removeAttribute('disabled');
		});
		rowImgs.forEach((img) => { img.style.pointerEvents = ''; img.style.opacity = ''; });
		return;
	}
	form.querySelectorAll('input, select, textarea, button').forEach((el) =>
	{
		if (el.type === 'hidden') return;
		if (el.tagName === 'BUTTON' && (el.classList.contains('bn_Escape') || el.classList.contains('bn_Enter'))) return;
		if (allowSelectors.some((sel) => { const n = form.querySelector(sel); return n && el === n; })) return;
		if (el.closest('.io-lock-allow')) return;
		el.setAttribute('data-io-lock-disabled', '1');
		el.setAttribute('disabled', 'disabled');
	});
	rowImgs.forEach((img) => { img.style.pointerEvents = 'none'; img.style.opacity = '0.45'; });
}

function _parsePaymentPrazos(raw) {
	if (Array.isArray(raw)) return raw.map((x) => Number(x));
	if (raw == null || raw === '') return [];
	if (typeof raw === 'string') {
		try {
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed.map((x) => Number(x)) : [];
		} catch (_) {
			return [];
		}
	}
	return [];
}

function _fmtDateBR(iso) {
	if (!iso) return '';
	const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return String(iso);
	return `${m[3]}/${m[2]}/${m[1]}`;
}

function _addDaysIso(iso, days) {
	const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (!m) return '';
	const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	d.setDate(d.getDate() + Number(days || 0));
	const yyyy = d.getFullYear();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
}

function syncPaymentPrazosUI(root) {
	if (!root) return;
	const countEl = root.querySelector('#payment_installment_count');
	const baseEl = root.querySelector('#payment_base_date');
	const hidden = root.querySelector('#payment_prazos');
	const created = root.querySelector('#created_on')?.value;
	let count = parseInt(String(countEl?.value || '').replace(/\D/g, ''), 10);
	if (!Number.isFinite(count) || count < 0) count = 0;
	if (count > 12) count = 12;
	const prazos = [];
	for (let i = 0; i < 12; i++) {
		const row = root.querySelector(`tr[data-prazo-idx="${i}"]`);
		const inp = root.querySelector(`#io_prazo_${i}`);
		if (row) row.style.display = i < count ? '' : 'none';
		if (i < count) {
			const days = parseInt(String(inp?.value || '').trim(), 10);
			prazos.push(Number.isFinite(days) ? days : 0);
			if (inp && (inp.value === '' || inp.value == null) && days === 0)
				inp.value = '0';
			const venctoEl = row?.querySelector('.io-vencto');
			const base = (baseEl?.value || '').trim()
				|| (created && String(created).match(/^\d{4}-\d{2}-\d{2}/)?.[0])
				|| '';
			if (venctoEl)
				venctoEl.textContent = base && Number.isFinite(days) ? _fmtDateBR(_addDaysIso(base, days)) : '—';
		}
	}
	if (hidden)
		hidden.value = count > 0 ? JSON.stringify(prazos) : '';
}

function bindPaymentPrazos(root) {
	if (!root || root._ioPaymentBound) return;
	root._ioPaymentBound = true;
	const sync = () => syncPaymentPrazosUI(root);
	root.querySelector('#payment_installment_count')?.addEventListener('input', sync);
	root.querySelector('#payment_installment_count')?.addEventListener('change', sync);
	root.querySelector('#payment_base_date')?.addEventListener('change', sync);
	for (let i = 0; i < 12; i++) {
		root.querySelector(`#io_prazo_${i}`)?.addEventListener('input', sync);
		root.querySelector(`#io_prazo_${i}`)?.addEventListener('change', sync);
	}
	sync();
}

function hydratePaymentPrazos(root, prazosRaw) {
	if (!root) return;
	const prazos = _parsePaymentPrazos(prazosRaw);
	const countEl = root.querySelector('#payment_installment_count');
	let count = parseInt(String(countEl?.value || '').replace(/\D/g, ''), 10);
	if (!Number.isFinite(count) || count <= 0)
		count = prazos.length;
	if (countEl && (!countEl.value || countEl.value === '0') && prazos.length)
		countEl.value = String(prazos.length);
	for (let i = 0; i < 12; i++) {
		const inp = root.querySelector(`#io_prazo_${i}`);
		if (inp)
			inp.value = i < prazos.length && Number.isFinite(prazos[i]) ? String(prazos[i]) : '';
	}
	syncPaymentPrazosUI(root);
}

export default {

	html: `
		<style>
			#order.io-order-form .io-order-header-scroll {
				flex-shrink: 0;
				overflow-x: hidden;
				padding-right: 0.2rem;
			}
			#order.io-order-form .io-order-tail {
				flex-shrink: 0;
			}
			#order.io-order-form .io-section-title {
				font-size: 0.75rem;
				font-weight: 600;
				letter-spacing: 0.02em;
				text-transform: uppercase;
				color: var(--color-muted, #555);
				margin: 0 0 0.4rem 0;
				grid-column: 1 / -1;
			}
			#order.io-order-form .io-grid {
				display: grid;
				grid-template-columns: repeat(4, minmax(0, 1fr));
				gap: 0.5rem 0.85rem;
				margin-bottom: 0.5rem;
				align-items: start;
			}
			#order.io-order-form .io-grid label,
			#order.io-order-form .io-grid > span {
				display: block;
				min-width: 0;
				margin-right: 0 !important;
				margin-bottom: 0 !important;
			}
			/* block (não flex column): evita quebra do <sup>*</sup> em linha separada no Fornecedor/Cliente */
			#order.io-order-form .io-grid > span > label {
				display: block;
				min-width: 0;
				margin: 0 !important;
			}
			#order.io-order-form .io-grid label.io-field--check {
				flex-direction: row;
				flex-wrap: wrap;
				align-items: center;
				gap: 0.35rem 0.5rem;
			}
			#order.io-order-form .io-grid label.io-field--check.io-devolucao-secondary {
				flex-direction: column;
				align-items: flex-start;
				align-self: start;
				gap: 0.35rem;
			}
			#order.io-order-form .io-devolucao-secondary .io-check-slot {
				min-height: 2.15rem;
				display: flex;
				align-items: center;
				box-sizing: border-box;
			}
			#order.io-order-form .io-grid label.io-field--check.io-devolucao-secondary input[type="checkbox"] {
				margin: 0;
			}
			#order.io-order-form .io-grid label.io-field--check input[type="checkbox"] {
				margin: 0;
				width: auto;
				display: inline-block;
			}
			#order.io-order-form .io-grid > span.io-party-wrap {
				display: block;
				min-width: 0;
				align-self: stretch;
			}
			#order.io-order-form .io-grid > span.io-party-wrap .sial-field-party {
				width: 100%;
			}
			#order.io-order-form .io-span-2 { grid-column: span 2; }
			#order.io-order-form .io-span-full { grid-column: 1 / -1; }
			#order.io-order-form .sial-items-scroll .io-col-product { width: 38%; }
			#order.io-order-form .sial-items-scroll .io-col-unit { width: 11%; }
			#order.io-order-form .sial-items-scroll .io-col-qty { width: 8%; }
			#order.io-order-form .sial-items-scroll .io-col-serial { width: 12%; }
			#order.io-order-form .sial-items-scroll .serial {
				min-width: 6.5rem;
				background: #fffbeb;
				border-color: #f59e0b;
			}
			#order.io-order-form .sial-items-scroll .io-col-price { width: 10%; }
			#order.io-order-form .sial-items-scroll .io-col-total { width: 10%; }
			#order.io-order-form .io-grand-total {
				text-align: right; font-weight: 600; font-size: 0.88rem;
				padding: 0.35rem 0.25rem 0; color: var(--color-text, #222);
				flex-shrink: 0;
			}
			#order.io-order-form #freight_value,
			#order.io-order-form #discount { text-align: right; }
			#order.io-order-form label.notes {
				display: flex;
				flex-direction: column;
				width: 100%;
				margin: 0.35rem 0 0 0 !important;
				flex-shrink: 0;
			}
			#order.io-order-form label.notes textarea {
				width: 100%;
				box-sizing: border-box;
			}
			#order.io-order-form .io-footer {
				display: flex;
				justify-content: space-between;
				align-items: center;
				width: 100%;
				flex-shrink: 0;
				margin-top: 0.5rem;
				padding-top: 0.75rem;
				border-top: 1px solid var(--color-border, #ccc);
				gap: 0.75rem;
				flex-wrap: wrap;
			}
			#order.io-order-form .io-footer .button { min-width: 5.5rem; }
			#io-lock-banner { margin: 0 0 0.75rem 0; padding: 0.5rem 0.65rem; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; color: #475569; }
			#order.io-order-form.input-order--edit-locked label:not(.io-lock-allow) { color: #94a3b8; }
		</style>
		<form id="order" class="form io-order-form sial-modal-form" autocomplete="off">
			<input type="hidden" id="edit_locked_by_input" value="0">
			<p id="io-lock-banner" class="fsxx" style="display:none">Este pedido possui Entrada gerada (não cancelada): os campos ficam bloqueados, exceto <strong>Transportador</strong>, <strong>Tipo/Valor do frete</strong> e <strong>Condição de pagamento</strong>. A edição plena volta quando todas as Entradas vinculadas forem canceladas.</p>
			<datalist id="companies">
			</datalist>
			<datalist id="suppliers">
			</datalist>
			<datalist id="customers">
			</datalist>
			<datalist id="carriers">
			</datalist>
			<datalist id="programs">
			</datalist>
			<datalist id="receipt_types">
			</datalist>
			<datalist id="products">
			</datalist>
			<datalist id="units">
			</datalist>
			<datalist id="users">
			</datalist>
			<div class="io-order-header-scroll">
			<div class="io-grid">
				<div class="io-section-title">Identificação</div>
				<label title="Empresa detentora do registro">Empresa<sup>*</sup>
					<input id="company_id" type="text" list="companies" required>
				</label>
				<label title="Identificador do pedido">Pedido
					<input id="id" type="text" disabled>
				</label>
				<label title="Código de referência externa">Referência
					<input id="reference" type="text">
				</label>
				<label title="Número/referência da NF de entrada (agrupador; consulta/BI)">NF
					<input id="nf_number" type="text" maxlength="120" placeholder="Ex.: 123456 / série-número">
				</label>
				<label title="Status do pedido">Status<sup>*</sup>
					<input id="status" type="text" list="order_status" value="10" required>
				</label>
				<label title="Data e hora de criação do pedido">Data criação
					<input id="created_on" type="text" disabled>
				</label>
				<label title="Usuário criador do pedido">Criado por
					<input id="created_by" type="text" list="users" disabled>
				</label>
				<label title="Data e hora de alteração do pedido">Data alteração
					<input id="updated_on" type="text" disabled>
				</label>
				<label title="Último usuário a alterar o pedido">Alterado por
					<input id="updated_by" type="text" list="users" disabled>
				</label>
			</div>
			<div class="io-grid">
				<div class="io-section-title">Fornecedor / logística</div>
				<span id="wrap_supplier_party" class="io-party-wrap io-span-2">
					<label class="sial-field-party" title="Fornecedor do pedido">Fornecedor<sup>*</sup>
						<input id="supplier_id" type="text" list="suppliers" required>
					</label>
				</span>
				<span id="wrap_customer_party" class="io-party-wrap io-span-2" style="display:none">
					<label class="sial-field-party" title="Cliente que devolve (devolução)">Cliente (devolução)<sup>*</sup>
						<input id="customer_id" type="text" list="customers">
					</label>
				</span>
				<label class="io-lock-allow" title="Transportador do pedido">Transportador
					<input id="carrier_id" type="text" list="carriers">
				</label>
				<label title="Programa sugerido para a entrada">Programa
					<input id="program_id" type="text" list="programs">
				</label>
				<label title="Número do empenho (Ano + NE + número, ex.: 2025NE02173)">Nº empenho
					<input id="empenho_number" type="text" placeholder="2025NE02173" maxlength="32">
				</label>
				<label title="Ordem de compra (OC) AMIL para rastreio operacional">OC Amil
					<input id="amil_oc" type="text" placeholder="Ex.: 4000222822" maxlength="60">
				</label>
				<label title="Tipo de recebimento (cadastro Tipos de Recebimento)" class="io-span-2">Tipo recebimento
					<input id="receipt_type_id" type="text" list="receipt_types">
				</label>
				<label title="Este pedido é uma devolução?" class="io-field--check io-devolucao-secondary">Devolução
					<span class="io-check-slot"><input id="is_return" type="checkbox"></span>
				</label>
			</div>
			<div class="io-grid">
				<div class="io-section-title">Frete</div>
				<label class="io-lock-allow" title="Tipo de frete">Tipo frete
					<input id="freight_type" type="text" list="freight_types">
				</label>
				<label class="io-lock-allow" title="Valor do frete">Valor frete
					<input id="freight_value" type="text">
				</label>
				<label title="Desconto sobre o valor do pedido">% desconto
					<input id="discount" type="text">
				</label>
			</div>
			<div class="io-grid io-payment-block">
				<div class="io-section-title">Condição de pagamento</div>
				<label class="io-lock-allow" title="Quantidade de parcelas (1 a 12)">Qtd parcelas
					<input id="payment_installment_count" type="text" inputmode="numeric" placeholder="Ex.: 3">
				</label>
				<label class="io-lock-allow" title="Data base para calcular os vencimentos (padrão: data de criação do pedido)">Data base venc.
					<input id="payment_base_date" type="date">
				</label>
				<input id="payment_prazos" type="hidden">
				<div class="io-payment-prazos-wrap" style="grid-column:1/-1;max-width:36rem;">
					<table class="io-payment-prazos" style="width:100%;border-collapse:collapse;font-size:0.85rem;">
						<thead>
							<tr>
								<th style="text-align:left;padding:2px 4px;">Parc.</th>
								<th style="text-align:right;padding:2px 4px;">Prazo (dias)</th>
								<th style="text-align:left;padding:2px 4px;">Vencimento</th>
							</tr>
						</thead>
						<tbody>
							<tr data-prazo-idx="0" style="display:none"><td>01</td><td style="text-align:right"><input id="io_prazo_0" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="1" style="display:none"><td>02</td><td style="text-align:right"><input id="io_prazo_1" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="2" style="display:none"><td>03</td><td style="text-align:right"><input id="io_prazo_2" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="3" style="display:none"><td>04</td><td style="text-align:right"><input id="io_prazo_3" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="4" style="display:none"><td>05</td><td style="text-align:right"><input id="io_prazo_4" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="5" style="display:none"><td>06</td><td style="text-align:right"><input id="io_prazo_5" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="6" style="display:none"><td>07</td><td style="text-align:right"><input id="io_prazo_6" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="7" style="display:none"><td>08</td><td style="text-align:right"><input id="io_prazo_7" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="8" style="display:none"><td>09</td><td style="text-align:right"><input id="io_prazo_8" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="9" style="display:none"><td>10</td><td style="text-align:right"><input id="io_prazo_9" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="10" style="display:none"><td>11</td><td style="text-align:right"><input id="io_prazo_10" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
							<tr data-prazo-idx="11" style="display:none"><td>12</td><td style="text-align:right"><input id="io_prazo_11" type="text" inputmode="numeric" style="width:5rem;text-align:right"></td><td class="io-vencto"></td></tr>
						</tbody>
					</table>
				</div>
			</div>
			</div>
			<div class="sial-items-panel">
				<div class="sial-items-title">Itens do pedido</div>
				<div class="sial-items-scroll">
					<table>
						<thead>
							<tr>
								<th class="sial-col-chk"></th>
								<th class="io-col-product">Produto<sup>*</sup></th>
								<th class="io-col-unit">Unidade<sup>*</sup></th>
								<th class="io-col-qty tar">Qtde<sup>*</sup></th>
								<th class="io-col-serial" title="Opcional — letras e números, até 20">Serial</th>
								<th class="io-col-price tar">Preço unit.<sup>*</sup></th>
								<th class="io-col-total tar">Valor Total</th>
							</tr>
						</thead>
						<tbody>
							<tr class="row" data-size="10">
								<td class="sial-col-chk">
									<input id="checkbox-0" type="checkbox">
								</td>
								<td title="Produto">
									<input id="product_id-0" class="product" type="text" list="products" required>
								</td>
								<td title="Unidade de venda">
									<input id="unit_id-0" class="unit" type="text" list="units" required>
								</td>
								<td class="tar" title="Quantidade">
									<input id="quantity-0" class="quantity" type="text" required>
								</td>
								<td title="Serial previsto opcional (letras e números, até 20)">
									<input id="serial-0" class="serial" type="text" maxlength="20" pattern="[A-Za-z0-9]*" placeholder="Opcional" autocomplete="off">
								</td>
								<td class="tar" title="Preço unitário do produto">
									<input id="unit_price-0" class="price" type="text" required>
								</td>
								<td class="tar" title="Valor total do item (qtde × preço unit.)">
									<input id="item_total-0" class="item_total" type="text" readonly tabindex="-1">
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
			<div class="io-order-tail">
				<div id="io-grand-total" class="io-grand-total"></div>
				<label class="notes" title="Observações sobre o pedido">Observações
					<textarea id="notes" rows="2"></textarea>
				</label>
			</div>
			<div class="io-footer">
				<span class="io-footer-tools">
					<img class="bn_Invert" src="img/check.svg" title="Inverter caixas de seleção">
					<img class="bn_Clear" src="img/clear.svg" title="Apagar conteúdo das linhas selecionadas">
					<img class="bn_Insert" src="img/add.svg" title="Anexar uma nova linha">
					<img class="bn_Remove" src="img/remove.svg" title="Remover linhas selecionadas">
					<img class="bn_MoveUp" src="img/up.svg" title="Mover linhas selecionadas para cima">
					<img class="bn_MoveDn" src="img/down.svg" title="Mover linhas selecionadas para baixo">
				</span>
				<span class="io-footer-actions">
					<button class='bn_Escape button' type="button">Fechar</button>
					<button class='bn_Enter button' type="submit">Salvar</button>
				</span>
			</div>
		</form>
	`,

		form: 
	{
		selector: '#order',
		partyVisibilitySync(formRoot)
		{
			if (!formRoot) return;
			const ret = formRoot.querySelector('#is_return');
			const ws = formRoot.querySelector('#wrap_supplier_party');
			const wc = formRoot.querySelector('#wrap_customer_party');
			const sup = formRoot.querySelector('#supplier_id');
			const cust = formRoot.querySelector('#customer_id');
			const on = !!(ret && ret.checked);
			if (ws) ws.style.display = on ? 'none' : '';
			if (wc) wc.style.display = on ? '' : 'none';
			if (sup) sup.required = !on;
			if (cust) cust.required = on;
		},
		afterEditSetValues(fs)
		{
			if (fs?.hasTables?.())
				fs.setTableHeight(10);
			const root = fs?.dialog?.getNode()?.querySelector(this.selector);
			this.partyVisibilitySync(root);
			applyInputOrderEditLock(fs);
			setTimeout(() => {
				const r = root || document.querySelector(this.selector);
				recalcOrderTotals(r);
				syncFormFieldTitles(r);
				const hidden = r?.querySelector('#payment_prazos');
				hydratePaymentPrazos(r, hidden?.value);
				bindPaymentPrazos(r);
				syncStatusApprovalLock(r);
				applyInputOrderEditLock(fs);
			}, 0);
		},
		init(formset)	// Must not be async as init seq must be preserved
		{
			formset.loadList('#companies', 'companies', { labels: ['nick'] });
			formset.loadList('#users', 'users');
			formset.loadList('#programs', 'programs', { labels: ['name'], value: 'id' });
			formset.loadList('#receipt_types', 'receipt_types', { labels: ['code', 'description'], value: 'id' });
			Object.getPrototypeOf(this).init.call(this);
			const root = formset.dialog?.getNode?.()?.querySelector(this.selector) || document.querySelector(this.selector);
			bindFormFieldTitleSync(root);
			if (root)
			{
				const ret = root.querySelector('#is_return');
				const sup = root.querySelector('#supplier_id');
				const cust = root.querySelector('#customer_id');
				const companyEl = root.querySelector('#company_id');
				const sync = () =>
				{
					const on = !!(ret && ret.checked);
					this.partyVisibilitySync(root);
					if (on && sup) sup.value = '';
					if (!on && cust) cust.value = '';
				};
				if (ret)
					ret.addEventListener('change', sync);
				this.partyVisibilitySync(root);
				const tbl = root.querySelector('.sial-items-scroll table');
				if (tbl)
					tbl.addEventListener('input', () => recalcOrderTotals(root));
				bindPaymentPrazos(root);
				if (companyEl)
					companyEl.addEventListener('change', () => syncStatusApprovalLock(root));
				syncStatusApprovalLock(root);
			}
		},
	},
	
	fields: [
		{ properties: { selector: '#edit_locked_by_input' }, types: [types.pubsub()] },
		{ properties: { selector: '#company_id' }, types: [types.pubsub(), types.datalist()] },
		{ properties: { selector: '#id' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#reference' }, types: [types.caps()] },
		{ properties: { selector: '#nf_number' }, types: [] },
		{ properties: { selector: '#created_on' }, types: [types.datetime()] },
		{ properties: { selector: '#created_by' }, types: [types.datalist()] },
		{ properties: { selector: '#updated_on' }, types: [types.datetime()] },
		{ properties: { selector: '#updated_by' }, types: [types.datalist()] },
		{ properties: { selector: '#supplier_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('suppliers', { labels: ['cnpj', 'nick'] })] },
		{ properties: { selector: '#customer_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('customers', { labels: ['nick', 'name', 'cnpj', 'cpf'] })] },
		{ properties: { selector: '#carrier_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('carriers', { labels: ['nick'] })] },
		{ properties: { selector: '#program_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('programs', { labels: ['name'] })] },
		{ properties: { selector: '#empenho_number' }, types: [types.caps(), types.empenho()] },
		{ properties: { selector: '#amil_oc' }, types: [types.caps()] },
		{ properties: { selector: '#receipt_type_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('receipt_types', { labels: ['code', 'description'] })] },
		{ properties: { selector: '#freight_type' }, types: [types.opt_freight_type(), types.datalist()] },
		{ properties: { selector: '#freight_value' }, types: [types.number(2)] },
		{ properties: { selector: '#discount' }, types: [types.number(2)] },
		{ properties: { selector: '#payment_installment_count' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#payment_base_date' }, types: [] },
		{ properties: { selector: '#payment_prazos' }, types: [] },
		{ properties: { selector: '#io_prazo_0' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_1' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_2' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_3' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_4' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_5' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_6' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_7' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_8' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_9' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_10' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#io_prazo_11' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#notes' }, types: [types.textarea()] },
		{ properties: { selector: '#is_return' }, types: [types.checkbox()] },
		{ properties: { selector: '#status' }, types: [types.opt_input_order_status(), types.datalist()] },
		{ properties: { selector: '#checkbox-0' }, types: [types.checkbox()] },
		{ properties: { selector: '#product_id-0' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('products', { labels: ['sku', 'name'], filter: r => !r.is_disabled })] },
		{ properties: { selector: '#unit_id-0' }, types: [types.pubsub(), types.datalist(), types.observer(['company_id', 'product_id-0']), types.obs_unit(1)] },
		{ properties: { selector: '#quantity-0' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#serial-0' }, types: [types.caps()] },
		// Até 10 casas (MS/SUS), sem completar zeros visuais desnecessários.
		{ properties: { selector: '#unit_price-0' }, types: [types.number(10, 0, ',', '.', false)] },
		{ properties: { selector: '#item_total-0' }, types: [] },
	]

};

